import { Drawer, Form, Input, Row } from "antd";
import * as AUTH_SERVICES from "api/services/auth/auth.services";
import { StatusCode, UserRoles } from "common/enums";
import AppButton from "components/libs/button";
import { EmailAuthProvider, getAuth, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "store/auth/authSlice";
import s from "./password.module.css";

type Props = {
    visible: boolean,
    onClose: () => void,
}

const UpdatePasswordDrawer: React.FC<Props> = ({ visible, onClose }) => {

    const { newPasswordRequired } = useSelector((state) => state.app);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);

    //!HOOKS
    const { t } = useTranslation();
    const dispatch = useDispatch();
    //const auth = useSelector((state: RootStateOrAny) => state.auth);
    const [formInfos] = Form.useForm();
    const { user } = useSelector((state) => state.auth);

    const isStudent = user?.roles?.includes(UserRoles.STUDENT);
    const isTeacher = user?.roles?.includes(UserRoles.TEACHER);
    const isParent = user?.roles?.includes(UserRoles.PARENT);
    const isOrganism = user?.roles?.includes(UserRoles.ORGANISME);

    //!STATES
    const [isLoading, setIsLoading] = useState<boolean>(false);

    //!CONSTANTS
    const validateMessages = {
        required: t("auth.error.required"),
        types: {
            email: t("auth.error.emailInvalid"),
        },
    };

    const closeDrawer = async () => {
        onClose();
    }

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [error, setError] = useState<any>(null);
    const [successMessage, setSuccessMessage] = useState<string>("");

    const handleUpdatePassword = async () => {

        setIsLoading(true);

        try {
            const auth = getAuth();
            const firebaseUser = auth.currentUser;
            const credential = EmailAuthProvider.credential(user?.email, currentPassword);
            await reauthenticateWithCredential(firebaseUser!, credential);
            await updatePassword(firebaseUser!, newPassword);

            const finalData = {
                password: newPassword,
                requireNewPassword: false
            };

            if (isTeacher) {
                const response = await AUTH_SERVICES.updateTeacher(user?._id, { ...finalData, });
                if (response?.statusCode === StatusCode.OK) {
                    dispatch(
                        updateUser({ ...user, password: newPassword })
                    );
                    setSuccessMessage('Mot de passe mis à jour avec succès!');
                    setIsLoading(false);
                }
            }
            else if (isStudent) {
                const response = await AUTH_SERVICES.updateStudent(user?._id, { ...finalData, });
                if (response?.statusCode === StatusCode.OK) {
                    dispatch(
                        updateUser({ ...user, password: newPassword, requireNewPassword: false })
                    );
                    setSuccessMessage('Mot de passe mis à jour avec succès!');
                    setIsLoading(false);
                }
            }
            else {
                const response = await AUTH_SERVICES.updateParentOrganisme(user?._id, { ...finalData, });
                if (response?.statusCode === StatusCode.OK) {
                    dispatch(
                        updateUser({ ...user, password: newPassword })
                    );
                    setSuccessMessage('Mot de passe mis à jour avec succès!');
                    setIsLoading(false);
                }
            }
        } catch (error) {
            setError(error);
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Drawer
                title={<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end" }}>
                    <div style={{ textTransform: "uppercase", fontSize: "1.2em", margin: "0px auto" }}>{"Modifier le mot de passe"}</div>
                </div>}
                placement="right"
                closable={newPasswordRequired ? false : true}
                onClose={closeDrawer}
                visible={visible}
                width={isMobile ? "100%" : "70%"}
                headerStyle={{ display: "flex", justifyContent: "space-between" }}
                bodyStyle={{ background: "#FFF", padding: "0px" }}
                maskClosable={!newPasswordRequired}
            >
                <div style={{ alignItems: "flex-start", margin: "40px auto" }}>
                    <Form
                        validateTrigger={["onFinish"]}
                        validateMessages={validateMessages}
                        name="registerParent"
                        autoComplete="off"
                        form={formInfos}
                        onFinish={handleUpdatePassword}
                    >
                        <div style={{ width: "55%", margin: "0px auto" }}>

                            <Form.Item
                                name="oldPassword"
                                rules={[{ required: true }]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input.Password
                                    className={s.field}
                                    placeholder={"Ancien mot de passe"}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    { required: true },
                                    {
                                        pattern: /^.{6,}$/,
                                        message: "Le mot de passe doit contenir au moins 6 caractères !",
                                    },
                                ]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input.Password
                                    className={s.field}
                                    placeholder={"Nouveau mot de passe"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                rules={[
                                    { required: true },
                                    ({ getFieldValue }) => ({
                                        validator(_: any, value: any) {
                                            if (!value || getFieldValue("password") === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                new Error("Les mots de passe ne correspondent pas :(")
                                            );
                                        },
                                    }),
                                ]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input.Password
                                    className={s.field}
                                    placeholder={t("auth.confirmPassword")}
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                />
                            </Form.Item>
                        </div>


                        <Row justify="center" style={{ marginTop: "30px" }}>
                            <Form.Item>
                                <AppButton loading={isLoading} htmlTypeSubmit={true}><a className={s.nextButton}>Modifier</a></AppButton>
                            </Form.Item>
                        </Row>
                    </Form>
                    {error && <div style={{ color: 'red', margin: "0px auto", textAlign: "center" }}>{error}</div>}
                    {successMessage && <div style={{ color: 'green', margin: "0px auto", textAlign: "center" }}>{successMessage}</div>}
                </div>

            </Drawer>
        </div>
    );

};

export default UpdatePasswordDrawer;